// ============================================================================
// Elicitation schema → form field descriptors (pure, no DOM)
// ============================================================================
// Maps an ACP elicitation/create `requestedSchema` (JSON Schema object with
// primitive-typed properties) into a flat list of field descriptors that
// elicitation.js renders as DOM controls. Kept DOM-free so unit tests can
// import it directly (same pattern as markdown.js).

const SENSITIVE_NAME_RE = /passw|secret|token|api[_-]?key|credential|private[_-]?key|ssn|pin\b/i;

/** True when a field name or its property schema marks the value sensitive. */
export function isSensitiveField(name, prop) {
  if (SENSITIVE_NAME_RE.test(name)) return true;
  if (prop && typeof prop === "object") {
    if (prop.format === "password" || prop.writeOnly === true) return true;
  }
  return false;
}

/** Normalize an options list from any wire shape: strings, {const,value},
 * {label}, {title} or {name} entries. Returns [{value,label}] or null. */
function normOptions(list) {
  if (!Array.isArray(list) || !list.length) return null;
  const opts = [];
  for (const v of list) {
    if (typeof v === "string" || typeof v === "number") {
      opts.push({ value: String(v), label: String(v) });
    } else if (v && typeof v === "object") {
      const val = v.const ?? v.value ?? v.name ?? v.label ?? v.title;
      if (val == null) continue;
      opts.push({ value: String(val), label: String(v.title ?? v.label ?? v.name ?? val) });
    }
  }
  return opts.length ? opts : null;
}

/** Extract select options from enum / oneOf / options / items.enum — every
 * shape backends have been seen to emit (incl. Devin's options array). */
function enumOptions(prop) {
  return normOptions(prop.enum)
      ?? normOptions(prop.oneOf)
      ?? normOptions(prop.anyOf)
      ?? normOptions(prop.options)
      ?? normOptions(prop.items?.enum)
      ?? normOptions(prop.items?.oneOf)
      ?? normOptions(prop.items?.anyOf);
}

/** True when the property asks for multiple selections. */
function isMulti(prop) {
  return prop.type === "array" || prop.multi_select === true || prop.multiSelect === true;
}

/** Convert one property schema into a field descriptor. */
function propToField(name, prop, required) {
  const p = prop && typeof prop === "object" ? prop : {};
  const sensitive = isSensitiveField(name, p);
  const field = {
    name,
    label: p.title || name,
    required,
    sensitive,
    description: typeof p.description === "string" ? p.description : undefined,
    default: p.const !== undefined ? String(p.const) : p.default !== undefined ? p.default : undefined,
  };
  // Choice fields first: any options-bearing property becomes select or
  // multiselect regardless of the declared primitive type.
  const opts = enumOptions(p);
  if (opts) {
    if (isMulti(p)) {
      field.kind = "multiselect";
    } else {
      field.kind = "select";
    }
    field.options = opts;
    // Backends (e.g. Devin's ask_user_question) may allow a free-text
    // "Other" answer alongside the options.
    if (p.custom_text === true || p.customText === true) field.allowCustom = true;
    return field;
  }
  switch (p.type) {
    case "string": {
      if (sensitive || p.format === "password") {
        field.kind = "password";
      } else if (p.format === "textarea" || (typeof p.maxLength === "number" && p.maxLength > 500)) {
        field.kind = "textarea";
      } else {
        field.kind = "text";
        if (p.format) field.inputType = p.format === "email" ? "email" : p.format === "uri" ? "url" : "text";
      }
      break;
    }
    case "number":
    case "integer":
      field.kind = "number";
      if (typeof p.minimum === "number") field.min = p.minimum;
      if (typeof p.maximum === "number") field.max = p.maximum;
      if (typeof p.multipleOf === "number") field.step = p.multipleOf;
      else if (p.type === "integer") field.step = 1;
      break;
    case "boolean":
      field.kind = "checkbox";
      break;
    case "array":
      field.kind = "textlist";
      break;
    default:
      // Custom/future property schema types (incl. `_`-prefixed) degrade
      // to a text input rather than being dropped.
      field.kind = "text";
  }
  return field;
}

/**
 * Build field descriptors from an elicitation requestedSchema.
 * Returns [{ name, label, kind, required, sensitive, options?, description?,
 *           placeholder? }]. Unknown/unsupported property types degrade to
 * a plain text input. Field count is clamped for safety.
 */
export function buildFormFields(schema, { maxFields = 20 } = {}) {
  if (!schema || typeof schema !== "object") return [];
  const props = schema.properties;
  if (!props || typeof props !== "object") {
    // Bare single-question schema (no properties wrapper): usable only
    // when the top level declares options or a non-object primitive type —
    // an empty "{type:'object'}" still maps to no fields.
    const t = schema.type;
    if (enumOptions(schema) || (typeof t === "string" && t !== "object")) {
      return [propToField("answer", schema, false)];
    }
    return [];
  }
  const required = new Set(Array.isArray(schema.required) ? schema.required : []);
  const fields = [];
  for (const [name, prop] of Object.entries(props)) {
    if (fields.length >= maxFields) break;
    fields.push(propToField(name, prop, required.has(name)));
  }
  return fields;
}

/**
 * Coerce a field's DOM value back into the schema's type for the
 * elicitation response content object.
 */
export function coerceFieldValue(field, raw) {
  switch (field.kind) {
    case "number": {
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    }
    case "checkbox":
      return !!raw;
    case "multiselect":
      return Array.isArray(raw) ? raw.map(String) : [];
    case "textlist":
      return String(raw ?? "")
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
    default:
      return raw == null ? "" : String(raw);
  }
}
